import { formatMoney as formatMoneyValue, formatQty as formatQtyValue, type Money, type Qty } from "@autorepair/contracts";

export { parseMoneyInput } from "@autorepair/contracts";

/** contracts' own formatMoney() requires a real Money value — most places
 * in this app hold a `Money | null` (an optional list price, an unused
 * shop-supplies cap), so this adds the one bit of null-handling every
 * caller here actually needs. */
export function formatMoney(amount: Money | null | undefined): string {
  return amount == null ? "—" : formatMoneyValue(amount);
}

/** Same null-handling wrapper as formatMoney() above — a sublet line's
 * quantity is always null (see WorkOrderEditorPage.tsx), unlike a part
 * line's, which is required. */
export function formatQty(qty: Qty | null | undefined): string {
  return qty == null ? "—" : formatQtyValue(qty);
}

const QTY_RE = /^-?\d+(\.\d{1,3})?$/;

/** Same idea as contracts' own parseMoneyInput, for the one Qty field the
 * UI collects free-hand (a part line's quantity) — kept here rather than
 * in the shared package since it's this thin. */
export function parseQtyInput(raw: string): Qty | null {
  const trimmed = raw.trim();
  return QTY_RE.test(trimmed) ? (trimmed as Qty) : null;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const datePart = iso.length === 10 ? iso : iso.slice(0, 10);
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return datePart;
  return `${month}/${day}/${year}`;
}

/** `vehicleDescription`/`vehicleDescriptionSnapshot` carry the trim on a
 * second line (a literal "\n", separated so the PDF can print it on its
 * own line — see buildVehicleDescription() in apps/api/src/common/
 * pdf-identity.ts) — list rows don't want the trim at all, just the
 * "{year} {make} - {model}" first line. */
export function firstLine(value: string | null | undefined): string {
  if (!value) return "";
  return value.split("\n")[0]!;
}

/** Same source string as firstLine(), but for a spot that wants even
 * less — just "{year} {make}", not the model too (see
 * buildVehicleDescription()'s own "{year} {make} - {model}" format for
 * line 1). Splits on the FIRST " - " only, so a make/model that itself
 * contains " - " doesn't get truncated mid-name. */
export function yearAndMake(value: string | null | undefined): string {
  const line = firstLine(value);
  const separatorIndex = line.indexOf(" - ");
  return separatorIndex === -1 ? line : line.slice(0, separatorIndex);
}

/** Work order/invoice numbers print as 5-digit zero-padded ("00001") — a
 * purely cosmetic display format; the underlying number is never sent
 * back to the server in this form. */
export function formatDocNumber(n: number): string {
  return String(n).padStart(5, "0");
}

/** "INV-" prefix, same convention as estimate numbers' own "EST-" (see
 * EstimatesService.issue()) — unlike estimates, applied purely at
 * display time here, never stored (invoiceNumber stays a bare, sortable
 * integer on the wire). */
export function formatInvoiceNumber(n: number): string {
  return `INV-${formatDocNumber(n)}`;
}

/** Same idea as formatInvoiceNumber() above, for work order numbers —
 * "W" prefix, no dash (per the owner's own request), also display-only. */
export function formatWorkOrderNumber(n: number): string {
  return `W${formatDocNumber(n)}`;
}

/** "0.13000" -> "13%", "0.08250" -> "8.25%" — like formatRatePercent() in
 * apps/api/src/common/pdf-identity.ts (a `rate()` column is always exactly
 * 5 decimal places, so this is exact, no floating-point noise to guard
 * against), but trims trailing zeros — the PDF's own line always shows
 * 2 decimals ("13.00%"), while this compact in-app label reads better
 * without them for the common case of a round number. */
function formatRatePercent(rate: string): string {
  return `${(Number(rate) * 100).toFixed(2).replace(/\.?0+$/, "")}%`;
}

/** "(13%)" for a single qualifying rate, or "(Part 13%, Labor 5%)" once
 * more than one qualifies (needed to disambiguate which is which) — for
 * the work-order editor's own Totals card, which (unlike the PDF's
 * per-category tax line, see buildTaxRateLine() in apps/api/src/common/
 * pdf-identity.ts) has no "show on PDF" toggle to respect: it's an
 * internal shop view, not a customer-facing document. A category
 * qualifies only when its rate is nonzero AND it has at least one
 * TAXABLE line (`hasTaxableLine`) — a category made up entirely of
 * individually tax-exempt lines (or with no line at all) contributes
 * nothing to the tax total, so showing its rate would be misleading, same
 * reasoning as buildTaxRateLine()'s own `hasTaxableLine` param. Null when
 * nothing qualifies, so a shop with no sales tax configured (or a work
 * order with nothing taxable on it) doesn't get a misleading "(0%)" next
 * to its Tax row. Shared by the work-order editor's Totals card and the
 * invoice detail page's own totals — each just wraps this with its own
 * field names (a work order's *TaxRateSnapshot columns, an invoice's own
 * frozen *TaxRate columns). `labels` defaults to the auto_repair names,
 * same convention as buildTaxRateLine()'s own default, so a detailing
 * tenant sees "(Package 13%, Service 5%)" instead. */
function formatTaxRateLabel(
  parts: string,
  labor: string,
  sublet: string,
  labels: { parts: string; labor: string; sublet: string } = { parts: "Part", labor: "Labor", sublet: "Sublet" },
  hasTaxableLine: { parts: boolean; labor: boolean; sublet: boolean } = { parts: true, labor: true, sublet: true },
): string | null {
  const qualifying = (
    [
      [labels.parts, parts, hasTaxableLine.parts],
      [labels.labor, labor, hasTaxableLine.labor],
      [labels.sublet, sublet, hasTaxableLine.sublet],
    ] as const
  ).filter(([, rate, hasTaxable]) => Number(rate) !== 0 && hasTaxable);
  if (qualifying.length === 0) return null;
  if (qualifying.length === 1) {
    const [, rate] = qualifying[0]!;
    return `(${formatRatePercent(rate)})`;
  }
  return `(${qualifying.map(([label, rate]) => `${label} ${formatRatePercent(rate)}`).join(", ")})`;
}

export function formatWorkOrderTaxRateLabel(
  rates: {
    partsTaxRateSnapshot: string;
    laborTaxRateSnapshot: string;
    subletTaxRateSnapshot: string;
  },
  labels?: { parts: string; labor: string; sublet: string },
  hasTaxableLine?: { parts: boolean; labor: boolean; sublet: boolean },
): string | null {
  return formatTaxRateLabel(
    rates.partsTaxRateSnapshot,
    rates.laborTaxRateSnapshot,
    rates.subletTaxRateSnapshot,
    labels,
    hasTaxableLine,
  );
}

export function formatInvoiceTaxRateLabel(
  rates: { partsTaxRate: string; laborTaxRate: string; subletTaxRate: string },
  labels?: { parts: string; labor: string; sublet: string },
  hasTaxableLine?: { parts: boolean; labor: boolean; sublet: boolean },
): string | null {
  return formatTaxRateLabel(rates.partsTaxRate, rates.laborTaxRate, rates.subletTaxRate, labels, hasTaxableLine);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
